import { FC, ReactNode } from 'react';
import { LoadingPage } from 'src/components/loading-page';
import {
  AggregateEuProvider,
  useAggregateEuRequest,
} from 'src/hooks/use-aggregate-eu';
import {
  CapacityManagementProvider,
  useCapacityManagementRequest,
} from 'src/hooks/use-capacity-management';
import { CognitoUserProvider } from 'src/hooks/use-cognito';
import {
  DirectFcfsFlagsProvider,
  useDirectFcfsFlagsValue,
} from 'src/hooks/use-direct-fcfs-flags';
import {
  FeatureFlagsProvider,
  useFeatureFlagsRequest,
} from 'src/hooks/use-feature-flags';
import { GdmFlagsProvider, useGdmFlagsValue } from 'src/hooks/use-gdm-flags';
import {
  MonolithCurrentUserProvider,
  useMonolithCurrentUserRequest,
} from 'src/hooks/use-monolith-user';
import {
  SystemInfoProvider,
  useSystemInfoRequest,
} from 'src/hooks/use-system-info';
import { TrFlagsProvider, useTrFlagsValue } from 'src/hooks/use-tr-flags';
import { useRemitReportingSubscriptionInfo } from 'src/pages/reporting/secondary-tradings/overview/use-remit-reporting-subscription-info';
import {
  DocumentExchangeProvider,
  useOptionalDocumentExchangeRequest,
} from 'src/pages/shippers/upload-documents/use-document-exchange';

export const GlobalResourcesProvider: FC<{
  children: ReactNode;
  fallback?: ReactNode;
}> = ({ children, fallback = <LoadingPage /> }) => {
  const [loadedAuth, auth] = useMonolithCurrentUserRequest();
  const [loadedFeatureFlags, featureFlags] = useFeatureFlagsRequest();
  const [loadedSystemInfo, systemInfo] = useSystemInfoRequest();
  const gdmFlags = useGdmFlagsValue();
  const trFlags = useTrFlagsValue();
  const directFcfsFlags = useDirectFcfsFlagsValue();
  const capacityManagement = useCapacityManagementRequest();
  const documentExchange = useOptionalDocumentExchangeRequest();
  const aggregateEu = useAggregateEuRequest();
  const subscriptionInfoRequest = useRemitReportingSubscriptionInfo('PRIMARY');

  if (
    loadedAuth &&
    loadedFeatureFlags &&
    loadedSystemInfo &&
    gdmFlags.loaded &&
    trFlags.loaded &&
    directFcfsFlags.loaded &&
    capacityManagement.loaded &&
    documentExchange.loaded &&
    (!subscriptionInfoRequest || subscriptionInfoRequest.loaded)
  ) {
    return (
      <MonolithCurrentUserProvider value={auth}>
        <CognitoUserProvider>
          <FeatureFlagsProvider value={featureFlags}>
            <SystemInfoProvider value={systemInfo}>
              <GdmFlagsProvider value={gdmFlags.value}>
                <TrFlagsProvider value={trFlags.value}>
                  <DirectFcfsFlagsProvider value={directFcfsFlags.value}>
                    <AggregateEuProvider value={aggregateEu.response?.data}>
                      <CapacityManagementProvider
                        value={capacityManagement.response?.data}
                      >
                        <DocumentExchangeProvider
                          value={documentExchange.response?.data}
                        >
                          {children}
                        </DocumentExchangeProvider>
                      </CapacityManagementProvider>
                    </AggregateEuProvider>
                  </DirectFcfsFlagsProvider>
                </TrFlagsProvider>
              </GdmFlagsProvider>
            </SystemInfoProvider>
          </FeatureFlagsProvider>
        </CognitoUserProvider>
      </MonolithCurrentUserProvider>
    );
  } else {
    return <>{fallback}</>;
  }
};
